import * as moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useFragment } from 'react-relay';
import 'moment/locale/de';
import { DetailFilter } from '@components/detailFilter';
import { LiveFilter } from '@components/liveFilter';
import { archiveListFragment } from './ArchiveList.relay';
import { ArchiveListYearGroup, filterCol, filterColByDetailField, groupDocumentsByYear } from './lib';
import type { ArchiveListPropsType } from './types';

export const ArchiveList: React.FC<ArchiveListPropsType> = ({ component: componentKey, location }) => {
  const component = useFragment(archiveListFragment, componentKey);

  const [liveFilterInfo, setLiveFilterInfo] = useState<{ [key: string]: string[] }>({});
  const [detailFilterInfo, setDetailFilterInfo] = useState<{ [key: string]: { key: string; filterVals: string[] } }>({});
  const [detailFilterVisible, setDetailFilterVisible] = useState(false);
  const [allVisible, setAllVisible] = useState(false);

  const liveFilterHandler = useCallback((cols: Array<string>, p: any): void => {
    const filterInfo = cols.reduce(
      (acc, col) => {
        acc[col] = [p.target.value];
        return acc;
      },
      {} as { [key: string]: string[] },
    );

    setLiveFilterInfo(filterInfo);
  }, []);

  const detailFilterHandler = useCallback((col: string, filterVals: Array<string>, key: string): void => {
    if (col == null) {
      setDetailFilterInfo({});
      setAllVisible(false);
    } else {
      setDetailFilterInfo((prev) => {
        const newFilterInfo = { ...prev };

        if (filterVals.length === 0 || filterVals.indexOf('all') > -1) {
          delete newFilterInfo[col];
        } else {
          newFilterInfo[col] = {
            key,
            filterVals,
          };
        }

        return newFilterInfo;
      });
    }
  }, []);

  const toggleDetailFilter = useCallback((e: any): void => {
    setDetailFilterVisible((prev) => !prev);
  }, []);

  const language = useMemo(() => {
    let lang = 'de';
    const loc = location;
    if (loc) {
      if (loc && typeof loc === 'string' && loc.substring(0, 3) === 'en/') {
        lang = 'en';
      } else if (loc.pathname?.substring(0, 4) === '/en/') {
        lang = 'en';
      }
    }
    if (location.state?.language) {
      lang = location.state.language.toLowerCase();
    }
    return lang;
  }, [location]);

  moment.locale(language);
  const dateFormats = ['DD.MM.YYYY', 'MM/DD/YYYY'];

  const { filteredDocuments, filteredAndGroupedDocuments, years } = useMemo(() => {
    const preparedDocuments = (component.documents || []).map((documentNode: any) => ({
      ...documentNode,
      year: moment.parseZone(documentNode?.date, dateFormats).format('YYYY'),
    }));

    const filtered = preparedDocuments
      .filter((doc) => filterCol(doc, liveFilterInfo))
      .filter((doc) => filterColByDetailField(doc, detailFilterInfo, 'year'))
      .filter((doc) => filterColByDetailField(doc, detailFilterInfo, 'doctypeheader'));

    const grouped = groupDocumentsByYear(filtered);
    const sortedYears = Object.keys(grouped).sort((a, b) => (Number.parseInt(a, 10) < Number.parseInt(b, 10) ? 1 : -1));

    return {
      filteredDocuments: filtered,
      filteredAndGroupedDocuments: grouped,
      years: sortedYears,
    };
  }, [component.documents, liveFilterInfo, detailFilterInfo]);

  return (
    <div className="relative mx-auto flex w-full max-w-[820px] flex-wrap items-start justify-between max-[1100px]:w-[calc(100vw-80px)] max-[1100px]:max-w-[565px] max-[600px]:w-[calc(100vw-40px)]">
      <h2 className="Base-title mb-10 w-full text-[40px] leading-[1.1] max-[600px]:text-[32px] max-[600px]:leading-10">{component.archiveTitle}</h2>
      <div className="mt-0 w-full max-[620px]:mt-5">
        <div className="flex flex-col w-full gap-2 tablet:flex-row flex-wrap items-center justify-between border-b border-[#d0d0d0] pb-5">
          {component.livefilter && (
            <LiveFilter
              liveFilter={component.livefilter}
              filterHandlerFn={liveFilterHandler}
              filterText={Object.values(liveFilterInfo)[0]?.[0] || ''}
              filterCols={['text', 'header']}
              className="w-full tablet:w-auto flex-1 min-w-[34%] rounded-[20px] border border-[#d0d0d0] bg-[url(/static/front/assets/other-icons/search.png)] bg-position-[right_8px_center] bg-no-repeat px-[15px] py-[5px] pr-[35px] text-sm focus-within:border-[#a0a0a0] focus-within:outline-none max-[620px]:mb-2.5"
            />
          )}
          <button type="button" onClick={toggleDetailFilter} className="relative z-10 cursor-pointer pr-[22px] text-left w-full tablet:w-auto text-sm text-accent no-underline">
            {detailFilterVisible ? component.filterbuttontextexpanded : component.filterbuttontext}
            <span
              aria-hidden={true}
              className={`absolute right-0 top-0 h-5 w-[25px] bg-[url(/static/front/assets/chevron/_chevron-down.svg)] bg-position-[right_5px_center] bg-no-repeat transition-transform max-[620px]:relative max-[620px]:inline-block ${detailFilterVisible ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
        {component.detailfilter && (
          <DetailFilter
            isVisible={detailFilterVisible}
            detailFilter={component.detailfilter}
            filterHandlerFn={detailFilterHandler}
            resetHandlerFn={detailFilterHandler}
            filterVals={detailFilterInfo}
            filteredCounter={filteredDocuments.length}
            hasResetOption={false}
            radio={false}
          />
        )}
      </div>
      <div className="mt-[50px] w-full">
        {years.length > 0 ? (
          years.map((year, index) => (
            <ArchiveListYearGroup key={year} documents={filteredAndGroupedDocuments[year]} yearLabel={component.yearLabel || ''} year={year} isVisible={allVisible} isFirst={index === 0} language={language} />
          ))
        ) : (
          <div className="my-4 mb-10 font-bold text-[#3b3b3b]">{component.noresultstext}</div>
        )}
      </div>
    </div>
  );
};
